/**
 * Tenant Admin SPA root component.
 *
 * Combines TenantHeader + TenantSidebar + content area (Outlet).
 * Provides authentication, theming, and error boundary wrappers.
 *
 * Ported from legacy Layout.tsx (frontend-tenant) -- adapted for verisa_v1.
 *
 * Layout structure:
 * +--------------------------------------------------+
 * | TenantHeader (57px, fixed, #FAFAFA)              |
 * +------------------+-------------------------------+
 * | TenantSidebar    |  Main Content Area            |
 * | (260px desktop)  |  (flex: 1)                    |
 * | (60px tablet)    |  <Outlet /> (React Router)    |
 * | (hidden mobile)  |                               |
 * +------------------+-------------------------------+
 *
 * @module apps/tenant/App
 * @see EPIC-WS-TENANT-ADMIN
 */
import React, { useCallback, useEffect, useState } from "react";
import { useRoutes, Navigate, useParams, useNavigate, type RouteObject } from "react-router-dom";
import { TenantNavigationRouter } from "./TenantNavigationRouter";
import { QueryClientProvider, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTheme } from "@mui/material/styles";
import type { ThemeOptions } from "@mui/material/styles";
import { VerisaThemeProvider } from "../../theme/ThemeProvider";
import {
  BrandingProvider,
  useBranding,
  type BrandingState,
} from "../../branding/BrandingProvider";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
} from "@mui/material";
import VisibleTextField, { anyBlank } from "../../components/shared/VisibleTextField";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { createWorkspace, getAgent, getAgents } from "../../api/workspace";
import { TenantAuthWrapper } from "./AuthWrapper";
import {
  IMPERSONATION_DESCRIPTOR_QUERY_KEY,
  exitImpersonationSession,
  impersonationDescriptorQuery,
  toBannerBinding,
  useImpersonationShellSignal,
} from "../../api/impersonation";
import {
  ActiveViewStrip,
  IMPERSONATION_BANNER_MIN_HEIGHT,
  type ActiveViewStripState,
} from "../../components/shared/ImpersonationBanner";
import { useAuth } from "../../store/AuthContext";
import { WorkspaceProvider, useWorkspace } from "../../store/WorkspaceContext";
import { AccessibilityProvider } from "../../contexts/AccessibilityContext";
import { ErrorBoundary } from "../../store/ErrorBoundary";
import TenantHeader from "../../components/tenant/TenantHeader";
import PlatformNoticeBar from "../../components/shared/PlatformNoticeBar";
import TenantSidebar, {
  TENANT_SIDEBAR_WIDTH,
  TENANT_SIDEBAR_COLLAPSED_WIDTH,
} from "../../components/tenant/TenantSidebar";
import { buildWorkspaceRoutes, buildAgentDetailRoute, SurfaceOutlet, SurfaceRouteTable } from "./routes";
import { createQueryClient } from "../../utils/queryClient";
import { describeFailure } from "../../utils/apiError";
import { ToastProvider } from "../../components/shared/ToastProvider";
import { AppFooter, type AppFooterState } from "../../components/shared/AppFooter";
import { useLocale } from "../../i18n/I18nProvider";
import { TenantAgentNounProvider } from "../../utils/agentNoun";
import InviteAcceptPage from "../../pages/tenant/InviteAcceptPage";
import { ReleaseFeaturesGate } from "../../feature-gate";
import LoadingState from "../../components/shared/LoadingState/LoadingState";
import {
  installSurfaceRefusalHandler,
  surfacesQueryOptions,
  SurfaceGate,
  useSurfaceSet,
  type SurfacesError,
} from "../../surfaces";

// ---------------------------------------------------------------------------
// TanStack Query client (Contract 9: Data Fetching)
// ---------------------------------------------------------------------------

const queryClient = createQueryClient();

// ---------------------------------------------------------------------------
// Theme
// ---------------------------------------------------------------------------

/**
 * Manage's own MUI component overrides.
 *
 * Previously these were baked into a static `createTheme({...lightThemeOptions})`
 * at module scope, which is exactly why no mode selection could reach this SPA
 * (STORY-FRONTEND-THEMING AC-001, recorded unmet 2026-08-07). They now travel as
 * a prop to `VerisaThemeProvider`, which applies them with the SAME per-component
 * REPLACE semantics the spread above had — so Manage renders byte-identically in
 * light mode and simply gains the ability to render dark.
 *
 * REPLACE, not deep-merge, is load-bearing: `baseComponents` gives MuiButton a
 * `borderRadius: 8` and MuiPaper a `borderRadius: 8` that Manage does NOT have
 * today. Deep-merging would restyle every button and every surface in the SPA.
 */
export const tenantComponentOverrides: ThemeOptions["components"] = {
  MuiButton: {
    styleOverrides: {
      root: { textTransform: "none", fontWeight: 500 },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: { backgroundImage: "none" },
    },
  },
  MuiCssBaseline: {
    styleOverrides: {
      body: { margin: 0, padding: 0 },
    },
  },
};

// ---------------------------------------------------------------------------
// Header height constant (must match TenantHeader)
// ---------------------------------------------------------------------------

const HEADER_HEIGHT = 57;

/**
 * The layout half of the tenant impersonation mount (SPEC-FRONTEND-RULE-095,
 * TASK-IMP-AUTHCONTEXT-SPA-WIRING-CHG-021). The banner is a FIXED strip above
 * the app shell at zIndex appBar + 1, so WHILE A BINDING IS LIVE everything
 * below it moves down by its height: the in-flow content by a larger top
 * margin, and the two fixed pieces of the shell - TenantHeader's AppBar and
 * TenantSidebar's drawer paper - by the same offset. With no binding the
 * offset is 0 and every value is exactly what it was.
 */
// EXPORTED FOR ITS OWN TEST (2026-09-23): the offset is half placement and half
// arithmetic, and the arithmetic half - header height PLUS the banner when a
// binding is live - is what a source-reading test cannot see.
export function useTenantShellOffset(): { contentTop: string; shellSx: object } {
  const bannerOffset = useImpersonationShellSignal().bannerShown
    ? IMPERSONATION_BANNER_MIN_HEIGHT
    : 0;
  const top = HEADER_HEIGHT + bannerOffset;
  return {
    contentTop: `${top}px`,
    shellSx: bannerOffset
      ? {
          "& .MuiAppBar-positionFixed": { top: `${bannerOffset}px` },
          "& .MuiDrawer-paper": { top: `${top}px`, height: `calc(100% - ${top}px)` },
        }
      : {},
  };
}

// ---------------------------------------------------------------------------
// Legal footer -- the two tenant mounts of the RULE-006a mount table
// ---------------------------------------------------------------------------

/**
 * Map the BrandingProvider's read state onto AppFooter's render state.
 *
 * All three provider states are mapped DELIBERATELY, one arm each (D-BR-17):
 * - `loading` -> `hidden`: nothing has been read yet, so nothing is claimed --
 *   neither a legal notice nor the statement that one is missing. The
 *   provider's 3 s AbortController bound makes this state temporary by
 *   construction, so no route can sit in it and quietly never show the notice.
 * - `ok` -> `ok`: AppFooter itself renders nothing when `enabled` is false or
 *   every field is empty (RULE-006b, SCENARIO-002).
 * - `unavailable` -> `unavailable`: the legal footer fails VISIBLE (RULE-001,
 *   D-BR-15). A bar that vanished because the read failed is a violation, which
 *   is why this arm is a rendered line and not a `hidden`.
 *
 * The switch is exhaustive and carries no `default`, so a fourth provider state
 * would be a compile error here rather than a silently dropped footer.
 *
 * @see SPEC-PLATFORM-BRANDING-RULE-001 / RULE-006
 * @see SPEC-PLATFORM-BRANDING-UI-003
 */
function footerState(state: BrandingState): AppFooterState {
  switch (state) {
    case "ok":
      return "ok";
    case "unavailable":
      return "unavailable";
    case "loading":
      return "hidden";
  }
}

// ---------------------------------------------------------------------------
// Tenant surfaces -- the post-auth bootstrap and the scoped failure state
// ---------------------------------------------------------------------------

/**
 * Starts the ONE read of `GET /api/v1/me/surfaces` as soon as tenant identity
 * exists (inside TenantAuthWrapper + WorkspaceProvider, SPEC-TENANT-SURFACES
 * API-001 / ARCH-005-IF-031), in parallel with - never waiting on - the public
 * release-toggle read, and registers the 403 `surface_not_available` handler on
 * the shared API client (RULE-015). It renders nothing. The prefetch uses the
 * owner module's options, so it is the same fetcher on the same key
 * (SPEC-FRONTEND-RULE-077), not a second one.
 */
function SurfacesBootstrap(): null {
  const qc = useQueryClient();
  useEffect(() => installSurfaceRefusalHandler(qc), [qc]);
  useEffect(() => {
    void qc.prefetchQuery(surfacesQueryOptions());
  }, [qc]);
  return null;
}

/** The typed reason of a failed surfaces read, named for the user (RULE-014; SPEC-FRONTEND-RULE-042). */
function surfacesFailureMessage(error: SurfacesError): string {
  switch (error.reason) {
    case "unauthenticated":
      return t`Your sign-in could not be verified (401), so the functions your organization configures stay hidden. Sign in again, then retry.`;
    case "forbidden":
      return t`Reading the list of your organization's functions was refused (403), so the functions your organization configures stay hidden.`;
    case "unavailable":
      return t`The list of your organization's functions is temporarily unavailable (503), so the functions your organization configures stay hidden.`;
    case "network":
      return t`The list of your organization's functions could not be reached (network error), so the functions your organization configures stay hidden.`;
    case "malformed":
      return t`The list of your organization's functions came back unreadable, so the functions your organization configures stay hidden.`;
    case "unexpected": {
      const status = error.status !== undefined ? String(error.status) : "?";
      return t`The list of your organization's functions could not be loaded (${status}), so the functions your organization configures stay hidden.`;
    }
  }
}

/**
 * The SCOPED fail-closed state (SPEC-TENANT-SURFACES RULE-014, SCEN-010),
 * rendered INSIDE both shell layouts so the header, the workspace switcher and
 * everything not gated on a surface keep working. It is deliberately NOT
 * ReleaseFeaturesGate's GateError, which blanks the whole SPA
 * (feature-gate/GateLoader.tsx). role="alert" announces it; the Retry is a
 * native button, reachable by Tab, and shows progress while the read is in
 * flight (SPEC-FRONTEND-RULE-045/046).
 */
export function SurfacesFailureNotice(): React.ReactElement | null {
  const { status, error, isFetching, refetch } = useSurfaceSet();
  if (status !== "error" || !error) return null;
  return (
    <Alert
      severity="warning"
      role="alert"
      sx={{ mb: 2 }}
      data-testid="surfaces-failure-notice"
      action={
        <Button color="inherit" size="small" onClick={refetch} disabled={isFetching}>
          {isFetching ? t`Retrying...` : t`Retry`}
        </Button>
      }
    >
      <AlertTitle>
        <Trans>Some functions are not available right now</Trans>
      </AlertTitle>
      {surfacesFailureMessage(error)}
      {isFetching && <LoadingState size="small" label={t`Retrying...`} />}
    </Alert>
  );
}

// ---------------------------------------------------------------------------
// TenantLayout -- Header + Sidebar + Content
// ---------------------------------------------------------------------------

/**
 * Inner layout component that renders the header, sidebar, and content area.
 * Separated so that it can use MUI useTheme / useMediaQuery inside ThemeProvider.
 */
export function TenantLayout(): React.ReactElement {
  const muiTheme = useTheme();
  const isTablet = useMediaQuery(muiTheme.breakpoints.between("sm", "md"));
  const { workspace, workspaces, switchWorkspace, refresh } = useWorkspace();
  const shellOffset = useTenantShellOffset();
  // The footer values come from the ONE branding reader mounted at the SPA root
  // (UI-004); this layout neither fetches them nor composes a per-tenant
  // variant -- there is no tenant override (D-BR-09).
  const { branding, state: brandingState } = useBranding();
  const { locale } = useLocale();

  const [mobileOpen, setMobileOpen] = useState(false);

  // Create-workspace dialog state. The header popover's "Create
  // workspace" button calls onWorkspaceCreate; we open this dialog
  // and POST /api/v1/workspaces on submit.
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createDescription, setCreateDescription] = useState("");
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  // SPEC-FRONTEND-RULE-097, D-378: a name or description that is only spaces or
  // invisible characters is a BLANK VALUE the gateway refuses. It is flagged on
  // the field and keeps Create disabled, decided by the ONE shared predicate
  // (never `.trim()`). Both are sent exactly as typed; an EMPTY description
  // keeps its meaning (absent, sent as undefined).
  const createNameBlank = anyBlank([createName]);
  const createDescriptionBlank = anyBlank([createDescription]);

  const handleCreateOpen = useCallback(() => {
    setCreateName("");
    setCreateDescription("");
    setCreateError(null);
    setCreateOpen(true);
  }, []);

  const handleCreateClose = useCallback(() => {
    if (createSubmitting) return;
    setCreateOpen(false);
  }, [createSubmitting]);

  const handleCreateSubmit = useCallback(async () => {
    if (createName === "") {
      setCreateError(t`Name is required`);
      return;
    }
    if (createNameBlank || createDescriptionBlank) return;
    setCreateSubmitting(true);
    setCreateError(null);
    try {
      const ws = await createWorkspace({
        name: createName,
        description: createDescription || undefined,
      });
      await refresh();
      switchWorkspace(ws.id);
      setCreateOpen(false);
    } catch (e) {
      setCreateError(describeFailure(e, t`Failed to create workspace`));
    } finally {
      setCreateSubmitting(false);
    }
  }, [createName, createDescription, createNameBlank, createDescriptionBlank, refresh, switchWorkspace]);

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev);
  };

  // Live sidebar width. TenantSidebar owns the value (it holds the drag state
  // and the persisted preference) and reports it here so the nav reservation
  // and the content area track a drag frame by frame instead of only on
  // remount. The initial value matches what TenantSidebar renders on first
  // paint for the current breakpoint, so there is no width flash.
  const [sidebarWidth, setSidebarWidth] = useState<number>(
    isTablet ? TENANT_SIDEBAR_COLLAPSED_WIDTH : TENANT_SIDEBAR_WIDTH,
  );
  const currentSidebarWidth = sidebarWidth;

  // Map Workspace[] → WorkspaceOption[] for the header switcher.
  const workspaceOptions = workspaces.map((w) => ({ id: w.id, name: w.name }));

  return (
    // PlatformNoticeBar is the FIRST child of an outer column, above
    // TenantHeader, so the CRA Art. 14(8) notice reads as platform-wide rather
    // than nested under one workspace's header (SPEC-OPERATIONS RULE-019,
    // TASK-SCR-NOTICE-MOUNT-TENANT). It renders null when no notice is active,
    // so the ordinary layout is unchanged.
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", ...shellOffset.shellSx }}>
      <PlatformNoticeBar />
      {/* THE ROW (RULE-020, D-BR-29). It holds the navigation beside the
          scrolling content and gives up exactly the height the bar below it
          takes, which is what the owner's "the sidebar has to move up so the
          footer fits under it" means structurally.

          THE HEADER OFFSET LIVES HERE, ONCE. The fixed TenantHeader used to be
          compensated twice - a `marginTop` on the content column and a
          `top: HEADER_HEIGHT` on the sidebar's viewport-fixed drawer paper -
          and the drawer joins this row in FLOW now (TenantSidebar, CHG-002), so
          the row is the one place that can offset both children. The old
          `height: "100%"` is gone with it: the row takes the slack the notice
          bar, the header offset and the bar leave, and nothing else.

          IT IS `shellOffset.contentTop`, NOT `HEADER_HEIGHT` (2026-09-23, owner
          bug: the staging tenant shell sat 57px too low with an inner
          scrollbar). Two branches moved this offset in opposite directions and
          git merged both silently: #1729 put it on this row, while the
          impersonation wiring, written against the older layout, kept it on the
          content column below. The row must carry the WHOLE offset - the fixed
          header PLUS the impersonation banner when a binding is live - because
          the banner pushes the header down too (useTenantShellOffset). Offsetting
          the column instead would lose the banner's height, and offsetting both
          is the bug this comment exists to stop coming back. */}
      <Box
        sx={{
          display: "flex",
          flexGrow: 1,
          minHeight: 0,
          marginTop: shellOffset.contentTop,
        }}
      >
      {/* Header */}
      <TenantHeader
        onMobileToggle={handleDrawerToggle}
        workspaceName={workspace?.name}
        workspaceId={workspace?.id}
        workspaces={workspaceOptions}
        onWorkspaceSwitch={switchWorkspace}
        onWorkspaceCreate={handleCreateOpen}
      />

      {/* Sidebar */}
      <Box
        component="nav"
        sx={{
          width: { sm: currentSidebarWidth },
          flexShrink: { sm: 0 },
          position: "relative",
          zIndex: (t) => t.zIndex.drawer + 2,
        }}
        aria-label={t`Tenant navigation`}
      >
        <TenantSidebar
          mobileOpen={mobileOpen}
          onMobileToggle={handleDrawerToggle}
          onWidthChange={setSidebarWidth}
        />
      </Box>

      {/* Main Content Area. The ONE scrolling region of this shell and the
          route's `main` landmark. It no longer holds the bar (RULE-020), but it
          STAYS A FLEX COLUMN with a growing routed region: that shape was first
          added to push a sticky in-column bar down (D-BR-20, now history), and
          it turned out to be what lets a `flex: 1` page root fill the region at
          all (#1706, 0e72713f9). So it outlived the reason it was added for.
          Its padding, its overflow and its landmark are unchanged, and the
          header offset moved up to the row - this column carries NO marginTop of
          its own, because the row above already gave up that height for both of
          its children (2026-09-23 double-offset bug). */}
      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          p: 3,
          overflow: "auto",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* The routed region is what grows, and it is ITSELF a flex column
            (#1706, after 0e72713f9): a page root that sizes with `flex: 1;
            minHeight: 0` only fills the region when its parent is a flex
            container, and a block wrapper here collapsed such a page to its
            content height. <Outlet /> renders no element of its own, so the
            growing box has to be here rather than an sx prop on the route
            element. */}
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          {/* The scoped surfaces failure (RULE-014): inside the shell, above
              the routed page, never instead of it. */}
          <SurfacesFailureNotice />
          {/* Loader / refusal not-found / routed page (RULE-014, RULE-015). */}
          <SurfaceOutlet homePath="/" />
        </Box>
      </Box>
      </Box>
      {/* The installation legal footer, THE FULL-WIDTH BOTTOM ROW of the shell
          (SPEC-PLATFORM-BRANDING RULE-020, D-BR-29, SCENARIO-032). It is the
          LAST CHILD of the shell's full-height column, a SIBLING of the row
          above, so it spans under BOTH the navigation and the content. D-BR-04's
          "always visible" is STRUCTURAL here: this column does not scroll, the
          content scrolls in its own region above, so neither `sticky` nor
          `fixed` is needed to keep the bar on screen - and `fixed` stays
          refused. This OVERTURNS D-BR-26, which put the bar inside the content
          column beside the navigation; do not move it back without a ruling. */}
      <AppFooter
        values={branding.legal_footer}
        state={footerState(brandingState)}
        locale={locale}
      />

      {/* Create-workspace dialog (triggered from header switcher popover). The
          registered section tenant.header.workspace-create gates it with the
          header's entry (TASK-TSURF-MESSAGE-ACTIONS-AND-SECTIONS). */}
      <SurfaceGate surface="tenant.header.workspace-create">
      <Dialog open={createOpen} onClose={handleCreateClose} maxWidth="sm" fullWidth>
        <DialogTitle><Trans>Create workspace</Trans></DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <VisibleTextField
            label={t`Name`}
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            required
            autoFocus
            fullWidth
            disabled={createSubmitting}
          />
          <VisibleTextField
            label={t`Description (optional)`}
            value={createDescription}
            onChange={(e) => setCreateDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
            disabled={createSubmitting}
          />
          {createError && (
            <Box sx={{ color: "error.main", fontSize: "0.875rem" }}>{createError}</Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateClose} disabled={createSubmitting}>
            <Trans>Cancel</Trans>
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateSubmit}
            disabled={
              createName === "" || createNameBlank || createDescriptionBlank || createSubmitting
            }
          >
            {createSubmitting ? t`Creating...` : t`Create`}
          </Button>
        </DialogActions>
      </Dialog>
      </SurfaceGate>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// AgentLayout -- Header + Agent content (no workspace sidebar)
// ---------------------------------------------------------------------------

/**
 * Layout for agent detail pages. Shares the same header as TenantLayout but
 * renders no workspace sidebar — the AgentDetailPage renders its own sidebar.
 * The header breadcrumb updates to show: Logo / Workspace / Agent Name.
 *
 * THE LEGAL FOOTER IS MOUNTED HERE AGAIN, AS THE SHELL'S BOTTOM ROW (D-BR-29,
 * RULE-020). This file has been the subject of two opposite rulings in one day,
 * so the trail matters. D-BR-26 moved the mount OUT of this layout and into the
 * agent shell's right-hand column (web/src/pages/workspace/AgentDetailPage.tsx),
 * reading the owner's correction as "align the bar with the innermost content
 * column, beside whatever navigation that surface has". D-BR-29 records that
 * this was a paraphrase of his words and the wrong reading: he meant the bar
 * should run UNDER the navigation, full width. So the mount comes back here,
 * and the FULL-WIDTH shape that D-BR-26 called the defect is the ruled shape.
 * The mount is the last child of this layout's full-height column, a SIBLING of
 * the routed region rather than a child of it, so it spans under the agent
 * navigation the shell draws inside that region.
 *
 * WHAT DID NOT COME BACK is the D-BR-26 landmark fix: the box below stays a
 * plain Box and NOT `component="main"`, because the innermost content region of
 * an agent route is the agent shell's own column, which claims that landmark.
 * Both claiming it rendered two nested <main>s on every agent route (RULE-046).
 */
export function AgentLayout(): React.ReactElement {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const { workspace, workspaces, switchWorkspace } = useWorkspace();
  const shellOffset = useTenantShellOffset();
  // The footer values come from the ONE branding reader mounted at the SPA root
  // (UI-004), read through context - this is not a second fetch, and the reader
  // that D-BR-26 put in AgentDetailPage was deleted with the mount (CHG-003), so
  // the tree still holds exactly one (SPEC-FRONTEND-RULE-077).
  const { branding, state: brandingState } = useBranding();
  const { locale } = useLocale();
  const [agentName, setAgentName] = useState<string>("");
  const [allAgents, setAllAgents] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    if (!agentId) return;
    getAgent(agentId)
      .then((a) => setAgentName(a.name))
      .catch((err) => { console.error("[TenantApp] Failed to load agent:", err); setAgentName(""); });
  }, [agentId]);

  // Scoped to the active workspace, and refetched when it changes
  // (TASK-BUGFIX-AGENTS-WORKSPACE-SCOPE): this dropdown sits next to the
  // workspace switcher, so an unscoped list offered agents from workspaces
  // the user was not in. The empty dependency array also meant it never
  // reran on a switch.
  const allAgentsWorkspaceId = workspace?.id;
  useEffect(() => {
    if (!allAgentsWorkspaceId) return;
    let cancelled = false;
    getAgents({ workspace_id: allAgentsWorkspaceId })
      .then((resp) => {
        if (cancelled) return;
        setAllAgents((resp.items ?? []).map((a) => ({ id: a.id, name: a.name })));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("[TenantApp] Failed to load agents:", err);
        setAllAgents([]);
      });
    return () => { cancelled = true; };
  }, [allAgentsWorkspaceId]);

  return (
    // The SECOND top-level layout needs the identical mount: without it a
    // tenant sitting on an agent screen never sees the notice.
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", ...shellOffset.shellSx }}>
      <PlatformNoticeBar />
      <TenantHeader
        agentName={agentName || undefined}
        agentId={agentId}
        agents={allAgents}
        onAgentSwitch={(id) => navigate(`/agents/${id}`)}
        workspaceName={workspace?.name}
        workspaceId={workspace?.id}
        workspaces={workspaces.map((w) => ({ id: w.id, name: w.name }))}
        onWorkspaceSwitch={switchWorkspace}
      />
      {/* THE ROW of this shell (RULE-020). A plain box, NOT `component="main"`:
          the innermost content region of an agent route is the agent shell's
          own column, which claims that landmark (see the header block). It is
          the routed region, and the agent shell draws its own navigation INSIDE
          it, so the bar below spans under that navigation - which is exactly
          what D-BR-29 ruled the bar must do. It gives up the height the bar
          takes, so the agent navigation ends above the bar. It keeps its own
          `marginTop` for the fixed TenantHeader: this layout has no separate row
          element, so this box IS the row that TenantLayout offsets. */}
      <Box
        sx={{
          flex: 1,
          marginTop: shellOffset.contentTop,
          overflow: "auto",
          minHeight: 0,
        }}
      >
        {/* The scoped surfaces failure (RULE-014), inside the agent shell too. */}
        <SurfacesFailureNotice />
        <SurfaceOutlet homePath="/agents" />
      </Box>
      {/* The installation legal footer, THE FULL-WIDTH BOTTOM ROW (RULE-020,
          D-BR-29, SCENARIO-032): last child of the full-height column, sibling
          of the routed region, never inside it. */}
      <AppFooter
        values={branding.legal_footer}
        state={footerState(brandingState)}
        locale={locale}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// TenantRouter -- uses useRoutes for nested route support
// ---------------------------------------------------------------------------

function TenantRouter(): React.ReactElement {
  // The resolved surface set the builders filter on (RULE-006 (2)); a new
  // answer rebuilds the tree, exactly as a release-toggle change does.
  const surfaces = useSurfaceSet();
  const table: RouteObject[] = [
    // Invitation acceptance — minimal layout, no sidebar
    {
      path: "/invite/:token",
      element: <InviteAcceptPage />,
    },
    // Agent detail routes — full-width layout (no workspace sidebar)
    {
      path: "/agents/:agentId/*",
      element: <AgentLayout />,
      children: [buildAgentDetailRoute(surfaces)],
    },
    // Workspace-level routes — standard layout with sidebar
    {
      path: "/*",
      element: <TenantLayout />,
      children: [
        ...buildWorkspaceRoutes(surfaces),
        { path: "*", element: <Navigate to="/" replace /> },
      ],
    },
  ];
  const routeElements = useRoutes(table);
  // SurfaceOutlet reads the same table to find the current route's surface (RULE-015).
  return <SurfaceRouteTable.Provider value={table}>{routeElements}</SurfaceRouteTable.Provider>;
}

// ---------------------------------------------------------------------------
// TenantActiveViewStrip -- the operator's own live view, on the tenant site
// ---------------------------------------------------------------------------

/**
 * How often the operator's OWN descriptor is re-read on the tenant site, in
 * ms: the SAME 60-second cadence as the admin site's strip and the session
 * shell's re-check poll (AuthContext `IMPERSONATION_RECHECK_INTERVAL_MS`).
 */
const ACTIVE_VIEW_POLL_MS = 60_000;

/**
 * THE TENANT-SIDE ACTIVE-VIEW STRIP MOUNT (TASK-IMP-TENANT-ADMIN-LEG CHG-006,
 * STORY AC-003, major finding M25). A tenant administrator who started a view
 * from the Members page lands on the TARGET's workspace SPA; this strip lets
 * them end it from their own site, with no client state at all.
 *
 * It MOUNTS TASK-IMP-ADMIN-ENTRY's shared `ActiveViewStrip` - the component is
 * reused, not re-authored - with the same wiring as the admin site's
 * `AdminActiveViewStrip`: the caller's OWN `GET /api/v1/me/impersonation`
 * through the owning module's query options, and the SELF-EXIT
 * (`DELETE /api/v1/impersonation/sessions/{ref}`, SPEC-GO-API-CONTRACT-039,
 * permission impersonation.start), never the platform-admin revoke - a tenant
 * admin holds no impersonation.admin, so a strip on the revoke would be inert.
 *
 * ONE DIFFERENCE FROM THE ADMIN SITE, AND WHY: this SPA can itself be a
 * landing surface (a platform admin viewing as a tenant admin). In that tab
 * the banner is the exit, so the strip stays out of the way: it reads nothing
 * and renders nothing while this tab is inside a binding.
 */
export function TenantActiveViewStrip(): React.ReactElement | null {
  const { authenticated, impersonation } = useAuth();
  const { bannerShown } = useImpersonationShellSignal();
  const insideBinding = Boolean(impersonation) || bannerShown;
  const queryClient = useQueryClient();
  const descriptor = useQuery({
    ...impersonationDescriptorQuery,
    enabled: authenticated && !insideBinding,
    refetchInterval: ACTIVE_VIEW_POLL_MS,
  });
  const [exitState, setExitState] = useState<"idle" | "exiting" | "error">("idle");
  const live = descriptor.data ?? null;
  const liveRef = live?.session_ref ?? null;

  // A different binding (or none) is a fresh start for the exit state.
  useEffect(() => {
    setExitState("idle");
  }, [liveRef]);

  const onExit = useCallback(() => {
    if (!live) return;
    setExitState("exiting");
    exitImpersonationSession(live.session_ref)
      .then(() => {
        // 204, 200 {already_ended} and 404 all mean "nothing of mine is live
        // under this reference any more"; re-read rather than assume.
        setExitState("idle");
        queryClient.setQueryData(IMPERSONATION_DESCRIPTOR_QUERY_KEY, null);
        void queryClient.invalidateQueries({ queryKey: IMPERSONATION_DESCRIPTOR_QUERY_KEY });
      })
      .catch(() => setExitState("error"));
  }, [live, queryClient]);

  if (!authenticated || insideBinding) return null;

  let state: ActiveViewStripState;
  if (descriptor.isError && !live) {
    state = "error";
  } else if (!live) {
    state = "none";
  } else if (exitState === "exiting") {
    state = "exiting";
  } else if (exitState === "error") {
    state = "error";
  } else {
    state = "active";
  }

  return <ActiveViewStrip binding={live ? toBannerBinding(live) : null} state={state} onExit={onExit} />;
}

// ---------------------------------------------------------------------------
// TenantApp -- root with providers
// ---------------------------------------------------------------------------

/**
 * Tenant Admin SPA root component.
 *
 * Wraps the layout in BrowserRouter, AuthProvider, VerisaThemeProvider and
 * ErrorBoundary. Routes are defined in ./routes.tsx. The provider supplies its
 * own CssBaseline, and carries this SPA's component overrides as a prop rather
 * than a locally-built static theme -- that static theme was why no mode
 * selection could reach Manage.
 */
/**
 * The shared theme provider with this SPA's overrides AND the installation brand
 * colours applied (SPEC-PLATFORM-BRANDING RULE-007).
 *
 * A separate component because the palette is CONTEXT the BrandingProvider
 * publishes: `useBranding()` has to be called BELOW that provider, and `TenantApp`
 * is the component that renders it. The subtree arrives as `children` so every
 * mount inside it - including the landed footer mounts - stays exactly where it
 * was. The `components` prop keeps its per-component REPLACE semantics, and the
 * colours enter the palette as literal hex (SPEC-FRONTEND-RULE-081).
 */
function BrandedThemeProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const { brandPalette } = useBranding();
  return (
    <VerisaThemeProvider
      components={tenantComponentOverrides}
      brandPalette={brandPalette}
    >
      {children}
    </VerisaThemeProvider>
  );
}

export function TenantApp(): React.ReactElement {
  return (
    <QueryClientProvider client={queryClient}>
      {/* The tenant SPA's scope for the agent noun (SPEC-FRONTEND-RULE-096
          (5), D-368): a shared component below this point renders the
          platform's word, the same component in the admin or workspace SPA
          the fixed one. It resolves through the one resolver on the one
          ['platform','features'] fetcher, so it must sit inside
          QueryClientProvider. */}
      <TenantAgentNounProvider>
        {/* The one reader of GET /api/v1/branding, ABOVE VerisaThemeProvider
            (SPEC-PLATFORM-BRANDING UI-004). It is a plain fetch, so sitting
            inside QueryClientProvider here is incidental, not a dependency.
            The document.title suffix is this SPA's own word, so it is a TRANSLATED
            Lingui string while the product name beside it is not (D-BR-08); the
            provider composes "<product name> - Management" with an ASCII hyphen
            (RULE-064). */}
        <BrandingProvider appSuffix={t`Management`}>
          {/* InnerThemeProvider renders its own <CssBaseline/>, so the local one is
              gone — two would apply the reset twice. */}
          <BrandedThemeProvider>
            <ErrorBoundary>
              <TenantNavigationRouter>
                <AccessibilityProvider agentId="app">
                  <TenantAuthWrapper>
                    <ToastProvider>
                      {/* The operator-side active-view strip: ABOVE
                          WorkspaceProvider and ReleaseFeaturesGate, so no
                          release toggle can strand a live view without its
                          exit (TASK-IMP-TENANT-ADMIN-LEG CHG-006). */}
                      <TenantActiveViewStrip />
                      <WorkspaceProvider>
                        <SurfacesBootstrap />
                        <ReleaseFeaturesGate>
                          <TenantRouter />
                        </ReleaseFeaturesGate>
                      </WorkspaceProvider>
                    </ToastProvider>
                  </TenantAuthWrapper>
                </AccessibilityProvider>
              </TenantNavigationRouter>
            </ErrorBoundary>
          </BrandedThemeProvider>
        </BrandingProvider>
      </TenantAgentNounProvider>
    </QueryClientProvider>
  );
}

export default TenantApp;
